import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Search, Mail, Phone, 
  ShoppingBag, DollarSign, Loader2, Eye, Heart 
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { token, loading: authLoading } = useAuth();

  useEffect(() => {
    // Wait for auth loading to complete before fetching
    if (authLoading) return;
    if (token) {
      fetchCustomers();
    }
  }, [page, search, authLoading, token]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (search) params.append('search', search);

      const response = await axios.get(`${API}/admin/customers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerDetail(response.data);
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCustomers();
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetail(customer.id);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-customers-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container-custom py-6">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-heading font-bold">Customer Management</h1>
              <p className="text-muted-foreground mt-1">View and manage customer information</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-2xl p-6 shadow-card mb-8">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="customer-search-input"
              />
            </div>
            <Button type="submit" className="btn-primary">Search</Button>
          </form>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Customers List */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
                <p className="text-muted-foreground">Loading customers...</p>
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-card">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-2xl font-heading font-bold mb-2">No customers found</h2>
                <p className="text-muted-foreground">
                  {search ? 'Try a different search term' : 'Customers will appear here once they register'}
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-medium">Customer</th>
                      <th className="text-left p-4 font-medium">Orders</th>
                      <th className="text-left p-4 font-medium">Total Spent</th>
                      <th className="text-left p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr 
                        key={customer.id} 
                        className={`border-t border-border hover:bg-muted/30 transition-colors ${
                          selectedCustomer?.id === customer.id ? 'bg-primary/5' : ''
                        }`}
                        data-testid={`customer-row-${customer.id}`}
                      >
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-muted-foreground">{customer.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-sm">
                            {customer.order_count} orders
                          </span>
                        </td>
                        <td className="p-4 font-semibold">
                          ₹{customer.total_spent?.toFixed(2) || '0.00'}
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => handleViewCustomer(customer)}
                            className="p-2 text-muted-foreground hover:text-primary transition-colors"
                            data-testid={`view-customer-${customer.id}`}
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 p-4 border-t border-border">
                    <Button
                      variant="outline"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-4 text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Customer Detail Panel */}
          <div className="lg:col-span-1">
            {selectedCustomer ? (
              <div className="bg-white rounded-2xl shadow-card p-6 sticky top-24">
                {detailLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
                  </div>
                ) : customerDetail ? (
                  <div className="space-y-6">
                    {/* Customer Info */}
                    <div className="text-center pb-6 border-b border-border">
                      <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        <span className="text-2xl font-bold text-primary">
                          {customerDetail.customer.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold">{customerDetail.customer.name}</h3>
                      <p className="text-muted-foreground text-sm">Customer since {new Date(customerDetail.customer.created_at).toLocaleDateString()}</p>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm">{customerDetail.customer.email}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-muted/50 rounded-xl p-4 text-center">
                        <ShoppingBag className="w-6 h-6 mx-auto text-primary mb-2" />
                        <p className="text-2xl font-bold">{customerDetail.order_count}</p>
                        <p className="text-xs text-muted-foreground">Orders</p>
                      </div>
                      <div className="bg-muted/50 rounded-xl p-4 text-center">
                        <DollarSign className="w-6 h-6 mx-auto text-primary mb-2" />
                        <p className="text-2xl font-bold">₹{customerDetail.total_spent?.toFixed(0)}</p>
                        <p className="text-xs text-muted-foreground">Spent</p>
                      </div>
                    </div>

                    {/* Recent Orders */}
                    {customerDetail.orders.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">Recent Orders</h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {customerDetail.orders.slice(0, 5).map((order) => (
                            <div 
                              key={order.id}
                              className="flex items-center justify-between p-3 bg-muted/30 rounded-lg text-sm"
                            >
                              <div>
                                <p className="font-mono text-xs">{order.id.slice(0, 8)}...</p>
                                <p className="text-muted-foreground text-xs">
                                  {new Date(order.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">₹{order.total_amount}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  order.payment_status === 'paid' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {order.payment_status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-card p-8 text-center sticky top-24">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Select a customer to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomers;
